import { ApiError, apiFetch, hasLiveApi } from '@/api/client'
import type { Role } from '@/data/types'

/** Lab `Member/searchHierarchyPage` request — keep `serchById` spelling. */
export type SearchHierarchyPageRequest = {
  filter?: string | string[]
  page?: number
  serchById?: boolean
  accessLevel?: string
}

const MEMBER_ID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

/** Hierarchy Search chip is a member UUID → Lab `serchById` path. */
export function isHierarchySearchMemberId(raw: string): boolean {
  return MEMBER_ID_RE.test(raw.trim())
}

/** Lab Hierarchy Search row — upline columns by access level name. */
export type HierarchySearchRow = {
  _id: string
  username: string
  accessLevel: string
  Player?: string
  PlayerId?: string
  PlayerparentId?: string
  PlayerCreatedAt?: string
  Master?: string
  MasterId?: string
  MasterCreatedAt?: string
  SuperMaster?: string
  SuperMasterId?: string
  SuperMasterCreatedAt?: string
  DoubleMaster?: string
  DoubleMasterId?: string
  DoubleMasterCreatedAt?: string
  TripleMaster?: string
  TripleMasterId?: string
  TripleMasterCreatedAt?: string
  CompanyMaster?: string
  CompanyMasterId?: string
}

export type SearchHierarchyPageResponse = {
  data: { data: HierarchySearchRow[]; total?: number }
  value: boolean
}

const LEVEL_KEYS = [
  'Player',
  'Master',
  'SuperMaster',
  'DoubleMaster',
  'TripleMaster',
  'CompanyMaster',
] as const

/** Normalize Lab / searchHierarchyNew exact-match row into HierarchySearchRow. */
export function normalizeHierarchySearchRow(
  raw: Record<string, unknown>,
): HierarchySearchRow | null {
  const id = String(raw._id ?? raw.id ?? '').trim()
  const username = String(raw.username ?? '').trim()
  if (!id && !username) return null

  const accessLevel = String(raw.accessLevel ?? '').trim() || inferAccessLevel(raw)
  const row: HierarchySearchRow = {
    _id: id || username,
    username: username || id,
    accessLevel,
  }

  for (const key of LEVEL_KEYS) {
    const name = raw[key]
    if (typeof name === 'string' && name.trim()) {
      row[key] = name.trim()
    }
    const idVal = raw[`${key}Id`]
    if (typeof idVal === 'string' && idVal.trim()) {
      ;(row as Record<string, string>)[`${key}Id`] = idVal.trim()
    }
    const created = raw[`${key}CreatedAt`]
    if (typeof created === 'string' && created.trim()) {
      ;(row as Record<string, string>)[`${key}CreatedAt`] = created.trim()
    }
  }

  if (typeof raw.PlayerparentId === 'string' && raw.PlayerparentId.trim()) {
    row.PlayerparentId = raw.PlayerparentId.trim()
  }

  // Prefix-only autocomplete rows only have username — promote to Player when leaf-like.
  if (!row.Player && row.username && (accessLevel === 'Player' || !accessLevel)) {
    row.Player = row.username
  }

  return row
}

function inferAccessLevel(raw: Record<string, unknown>): string {
  for (const key of LEVEL_KEYS) {
    if (typeof raw[key] === 'string' && raw[key]) return key
  }
  return 'Player'
}

export function normalizeHierarchySearchRows(
  list: unknown[],
): HierarchySearchRow[] {
  const out: HierarchySearchRow[] = []
  for (const item of list) {
    if (!item || typeof item !== 'object') continue
    const row = normalizeHierarchySearchRow(item as Record<string, unknown>)
    if (row) out.push(row)
  }
  return out
}

async function searchHierarchyPagePrimary(
  body: SearchHierarchyPageRequest,
): Promise<{ rows: HierarchySearchRow[]; total: number }> {
  const res = await apiFetch<SearchHierarchyPageResponse>('/v1/member/searchHierarchyPage', {
    method: 'POST',
    body: JSON.stringify(body),
  })
  if (!res.value) return { rows: [], total: 0 }
  const rows = normalizeHierarchySearchRows(res.data?.data ?? [])
  return {
    rows,
    total: res.data?.total ?? rows.length,
  }
}

/**
 * Fallback when `searchHierarchyPage` is not deployed yet — Lab exact path via
 * `searchHierarchyNew` with a string[] filter returns the upline chain.
 */
export async function searchHierarchyPageViaNew(
  body: SearchHierarchyPageRequest,
): Promise<{ rows: HierarchySearchRow[]; total: number }> {
  const filters = (Array.isArray(body.filter) ? body.filter : body.filter ? [body.filter] : [])
    .map((s) => (typeof s === 'string' ? s.trim() : ''))
    .filter(Boolean)
  if (!filters.length) return { rows: [], total: 0 }

  const res = await apiFetch<SearchHierarchyPageResponse>('/v1/member/searchHierarchyNew', {
    method: 'POST',
    body: JSON.stringify({
      filter: filters,
      page: body.page ?? 1,
    }),
  })
  if (!res.value) return { rows: [], total: 0 }
  const rows = normalizeHierarchySearchRows(res.data?.data ?? [])
  return { rows, total: rows.length }
}

/**
 * Lab `Member/searchHierarchyPage`. Falls back to `searchHierarchyNew` exact
 * match when the page endpoint is missing (404/405) so UAT still works.
 */
export async function searchHierarchyPage(
  body: SearchHierarchyPageRequest,
): Promise<{ rows: HierarchySearchRow[]; total: number }> {
  try {
    return await searchHierarchyPagePrimary(body)
  } catch (err) {
    if (err instanceof ApiError && (err.status === 404 || err.status === 405)) {
      return searchHierarchyPageViaNew(body)
    }
    throw err
  }
}

/**
 * Which upline columns the viewer should see (Lab hierarchy-search.html).
 * Master has no Hierarchy Search page; CM is blocked from this console.
 */
export function hierarchyColumnsForRole(role: Role | undefined): {
  id: 'Player' | 'Master' | 'SuperMaster' | 'DoubleMaster' | 'TripleMaster'
  header: string
  short: string
}[] {
  switch (role) {
    case 'triple-master':
      return [
        { id: 'Player', header: 'Player (P/L)', short: 'P' },
        { id: 'Master', header: 'Master (P/L)', short: 'M' },
        { id: 'SuperMaster', header: 'SM (P/L)', short: 'SM' },
        { id: 'DoubleMaster', header: 'DM (P/L)', short: 'DM' },
      ]
    case 'double-master':
      return [
        { id: 'Player', header: 'Player (P/L)', short: 'P' },
        { id: 'Master', header: 'Master (P/L)', short: 'M' },
        { id: 'SuperMaster', header: 'SM (P/L)', short: 'SM' },
      ]
    case 'super-master':
      return [
        { id: 'Player', header: 'Player (P/L)', short: 'P' },
        { id: 'Master', header: 'Master (P/L)', short: 'M' },
      ]
    default:
      return []
  }
}

/** Demo rows when hierarchy API is offline / demo session. */
export const DEMO_HIERARCHY_ROWS: HierarchySearchRow[] = [
  {
    _id: '00000000-0000-4000-8000-000000000101',
    username: 'p_alpha',
    accessLevel: 'Player',
    Player: 'p_alpha',
    PlayerId: '00000000-0000-4000-8000-000000000101',
    Master: 'm_north',
    MasterId: '00000000-0000-4000-8000-000000000051',
    SuperMaster: 'sm_west',
    SuperMasterId: '00000000-0000-4000-8000-000000000031',
    DoubleMaster: 'dm_core',
    DoubleMasterId: '00000000-0000-4000-8000-000000000021',
  },
  {
    _id: '00000000-0000-4000-8000-000000000102',
    username: 'p_bravo',
    accessLevel: 'Player',
    Player: 'p_bravo',
    PlayerId: '00000000-0000-4000-8000-000000000102',
    Master: 'm_south',
    MasterId: '00000000-0000-4000-8000-000000000052',
    SuperMaster: 'sm_west',
    SuperMasterId: '00000000-0000-4000-8000-000000000031',
    DoubleMaster: 'dm_core',
    DoubleMasterId: '00000000-0000-4000-8000-000000000021',
  },
  {
    _id: '00000000-0000-4000-8000-000000000051',
    username: 'm_north',
    accessLevel: 'Master',
    Master: 'm_north',
    MasterId: '00000000-0000-4000-8000-000000000051',
    SuperMaster: 'sm_west',
    SuperMasterId: '00000000-0000-4000-8000-000000000031',
    DoubleMaster: 'dm_core',
    DoubleMasterId: '00000000-0000-4000-8000-000000000021',
  },
]

export const DEMO_HIERARCHY_SUGGESTIONS = [
  'p_alpha',
  'p_bravo',
  'm_north',
  'm_south',
  'sm_west',
  'dm_core',
]

export function demoSearchHierarchyPage(
  names: string[],
): { rows: HierarchySearchRow[]; total: number } {
  const needles = names.map((n) => n.trim().toLowerCase()).filter(Boolean)
  if (!needles.length) return { rows: [], total: 0 }
  const rows = DEMO_HIERARCHY_ROWS.filter((row) => {
    const id = row._id.toLowerCase()
    const hay = [
      row.username,
      row.Player,
      row.Master,
      row.SuperMaster,
      row.DoubleMaster,
      row.TripleMaster,
    ]
      .filter(Boolean)
      .map((s) => s!.toLowerCase())
    return needles.some(
      (n) => n === id || hay.includes(n) || hay.some((h) => h.includes(n)),
    )
  })
  return { rows, total: rows.length }
}

export function demoSuggestUsernames(needle: string, exclude: string[] = []): string[] {
  const q = needle.trim().toLowerCase()
  const blocked = new Set(exclude.map((s) => s.toLowerCase()))
  return DEMO_HIERARCHY_SUGGESTIONS.filter((name) => {
    if (blocked.has(name.toLowerCase())) return false
    if (!q) return true
    return name.toLowerCase().includes(q)
  }).slice(0, 12)
}

export function canUseHierarchySearch(role: Role | undefined): boolean {
  return hierarchyColumnsForRole(role).length > 0
}

export function hierarchySearchLive(): boolean {
  return hasLiveApi()
}
